/**
 * usePerformanceData Hook
 * React Query hooks for fetching performance tracking data
 *
 * SECURITY: Query keys include userId to prevent cache sharing between users
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { performanceApi } from '../services/performance.service';
import { useAuth } from '../context/AuthContext';
import type {
  PerformanceData,
  PerformanceTrendPoint,
  TeamPerformanceSummary,
  DepartmentPerformanceSummary,
  OrganizationPerformanceSummary,
  PerformanceWeights,
  PerformanceQueryParams,
  PerformanceExportParams,
  PeriodType,
} from '../types';

// Query keys for cache management
// IMPORTANT: User-specific queries include userId to prevent cache sharing between users
export const performanceQueryKeys = {
  all: ['performance'] as const,

  // Individual performance - includes userId for cache isolation
  myPerformance: (userId: string, params: PerformanceQueryParams) =>
    [...performanceQueryKeys.all, 'me', userId, params] as const,
  myOKRPerformance: (userId: string, params: PerformanceQueryParams) =>
    [...performanceQueryKeys.all, 'me', userId, 'okrs', params] as const,
  myTrend: (userId: string, params: PerformanceQueryParams) =>
    [...performanceQueryKeys.all, 'me', userId, 'trend', params] as const,

  // Team performance - includes managerId for cache isolation
  teamPerformance: (managerId: string, params: PerformanceQueryParams) =>
    [...performanceQueryKeys.all, 'team', managerId, params] as const,
  directReportPerformance: (managerId: string, userId: string, params: PerformanceQueryParams) =>
    [...performanceQueryKeys.all, 'team', managerId, userId, params] as const,
  directReportOKRs: (managerId: string, userId: string, params: PerformanceQueryParams) =>
    [...performanceQueryKeys.all, 'team', managerId, userId, 'okrs', params] as const,

  // Organization performance - includes tenantId for cache isolation
  orgPerformance: (tenantId: string, params: PerformanceQueryParams) =>
    [...performanceQueryKeys.all, 'organization', tenantId, params] as const,
  departmentsPerformance: (tenantId: string, params: PerformanceQueryParams) =>
    [...performanceQueryKeys.all, 'organization', tenantId, 'departments', params] as const,
  managersPerformance: (tenantId: string, params: PerformanceQueryParams) =>
    [...performanceQueryKeys.all, 'organization', tenantId, 'managers', params] as const,
  departmentPerformance: (tenantId: string, departmentId: string, params: PerformanceQueryParams) =>
    [...performanceQueryKeys.all, 'department', tenantId, departmentId, params] as const,
  userPerformance: (tenantId: string, userId: string, params: PerformanceQueryParams) =>
    [...performanceQueryKeys.all, 'user', tenantId, userId, params] as const,

  // Configuration - includes tenantId for cache isolation
  weights: (tenantId: string) => [...performanceQueryKeys.all, 'settings', tenantId, 'weights'] as const,
};

// Default query params
const defaultParams: PerformanceQueryParams = {
  periodType: 'quarterly',
};

// ============================================
// INDIVIDUAL PERFORMANCE HOOKS
// ============================================

/**
 * Hook to fetch current user's performance
 * SECURITY: Uses userId from auth context for cache isolation
 */
export function useMyPerformance(params: PerformanceQueryParams = defaultParams, enabled = true) {
  const { user } = useAuth();
  const userId = user?.id || '';

  return useQuery<PerformanceData>({
    queryKey: performanceQueryKeys.myPerformance(userId, params),
    queryFn: async () => {
      const response = await performanceApi.getMyPerformance(params);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch performance data');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: enabled && !!userId,
  });
}

/**
 * Hook to fetch current user's OKR performance breakdown
 */
export function useMyOKRPerformance(params: PerformanceQueryParams = defaultParams, enabled = true) {
  const { user } = useAuth();
  const userId = user?.id || '';

  return useQuery<{ okrs: PerformanceData['okrs']; okrProgressScore: number }>({
    queryKey: performanceQueryKeys.myOKRPerformance(userId, params),
    queryFn: async () => {
      const response = await performanceApi.getMyOKRPerformance(params);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch OKR performance data');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!userId,
  });
}

/**
 * Hook to fetch current user's performance trend
 */
export function useMyTrend(params: PerformanceQueryParams = defaultParams, enabled = true) {
  const { user } = useAuth();
  const userId = user?.id || '';

  return useQuery<PerformanceTrendPoint[]>({
    queryKey: performanceQueryKeys.myTrend(userId, params),
    queryFn: async () => {
      const response = await performanceApi.getMyTrend(params);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch performance trend');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!userId,
  });
}

// ============================================
// TEAM PERFORMANCE HOOKS (MANAGERS/ADMINS)
// ============================================

/**
 * Hook to fetch team performance summary
 * SECURITY: Uses managerId from auth context for cache isolation
 */
export function useTeamPerformance(params: PerformanceQueryParams = defaultParams, enabled = true) {
  const { user } = useAuth();
  const managerId = user?.id || '';

  return useQuery<TeamPerformanceSummary>({
    queryKey: performanceQueryKeys.teamPerformance(managerId, params),
    queryFn: async () => {
      const response = await performanceApi.getTeamPerformance(params);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch team performance');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!managerId,
  });
}

/**
 * Hook to fetch specific direct report's performance
 */
export function useDirectReportPerformance(
  userId: string,
  params: PerformanceQueryParams = defaultParams,
  enabled = true
) {
  const { user } = useAuth();
  const managerId = user?.id || '';

  return useQuery<PerformanceData>({
    queryKey: performanceQueryKeys.directReportPerformance(managerId, userId, params),
    queryFn: async () => {
      const response = await performanceApi.getDirectReportPerformance(userId, params);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch direct report performance');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!userId && !!managerId,
  });
}

/**
 * Hook to fetch specific direct report's OKR breakdown
 */
export function useDirectReportOKRs(
  userId: string,
  params: PerformanceQueryParams = defaultParams,
  enabled = true
) {
  const { user } = useAuth();
  const managerId = user?.id || '';

  return useQuery<{ okrs: PerformanceData['okrs']; okrProgressScore: number }>({
    queryKey: performanceQueryKeys.directReportOKRs(managerId, userId, params),
    queryFn: async () => {
      const response = await performanceApi.getDirectReportOKRs(userId, params);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch direct report OKRs');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!userId && !!managerId,
  });
}

// ============================================
// ORGANIZATION PERFORMANCE HOOKS (ADMIN ONLY)
// ============================================

/**
 * Hook to fetch organization-wide performance
 * SECURITY: Uses tenantId from auth context for cache isolation
 */
export function useOrganizationPerformance(
  params: PerformanceQueryParams = defaultParams,
  enabled = true
) {
  const { user } = useAuth();
  const tenantId = user?.tenantId || '';

  return useQuery<OrganizationPerformanceSummary>({
    queryKey: performanceQueryKeys.orgPerformance(tenantId, params),
    queryFn: async () => {
      const response = await performanceApi.getOrganizationPerformance(params);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch organization performance');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!tenantId,
  });
}

/**
 * Hook to fetch all departments' performance
 */
export function useDepartmentsPerformance(
  params: PerformanceQueryParams = defaultParams,
  enabled = true
) {
  const { user } = useAuth();
  const tenantId = user?.tenantId || '';

  return useQuery<DepartmentPerformanceSummary[]>({
    queryKey: performanceQueryKeys.departmentsPerformance(tenantId, params),
    queryFn: async () => {
      const response = await performanceApi.getDepartmentsPerformance(params);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch departments performance');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!tenantId,
  });
}

/**
 * Hook to fetch specific department's performance
 */
export function useDepartmentPerformance(
  departmentId: string,
  params: PerformanceQueryParams = defaultParams,
  enabled = true
) {
  const { user } = useAuth();
  const tenantId = user?.tenantId || '';

  return useQuery<DepartmentPerformanceSummary>({
    queryKey: performanceQueryKeys.departmentPerformance(tenantId, departmentId, params),
    queryFn: async () => {
      const response = await performanceApi.getDepartmentPerformance(departmentId, params);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch department performance');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!departmentId && !!tenantId,
  });
}

/**
 * Hook to fetch any user's performance (admin only)
 */
export function useUserPerformance(
  userId: string,
  params: PerformanceQueryParams = defaultParams,
  enabled = true
) {
  const { user } = useAuth();
  const tenantId = user?.tenantId || '';

  return useQuery<PerformanceData>({
    queryKey: performanceQueryKeys.userPerformance(tenantId, userId, params),
    queryFn: async () => {
      const response = await performanceApi.getUserPerformance(userId, params);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch user performance');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!userId && !!tenantId,
  });
}

// ============================================
// CONFIGURATION HOOKS
// ============================================

/**
 * Hook to fetch current performance weights
 */
export function usePerformanceWeights() {
  const { user } = useAuth();
  const tenantId = user?.tenantId || '';

  return useQuery<PerformanceWeights>({
    queryKey: performanceQueryKeys.weights(tenantId),
    queryFn: async () => {
      const response = await performanceApi.getPerformanceWeights();
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch performance weights');
      }
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - weights rarely change
    enabled: !!tenantId,
  });
}

/**
 * Hook to update performance weights
 */
export function useUpdatePerformanceWeights() {
  const queryClient = useQueryClient();

  return useMutation<PerformanceWeights, Error, PerformanceWeights>({
    mutationFn: async (weights) => {
      const response = await performanceApi.updatePerformanceWeights(weights);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to update performance weights');
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all performance queries since weights changed
      queryClient.invalidateQueries({ queryKey: performanceQueryKeys.all });
    },
  });
}

// ============================================
// EXPORT HOOKS
// ============================================

/**
 * Hook to get performance data for PDF export
 */
export function usePerformanceExport() {
  return useMutation<
    PerformanceData | TeamPerformanceSummary | DepartmentPerformanceSummary | OrganizationPerformanceSummary,
    Error,
    PerformanceExportParams
  >({
    mutationFn: async (params) => {
      const response = await performanceApi.getPerformanceExportData(params);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to get performance export data');
      }
      return response.data;
    },
  });
}

// ============================================
// COMBINED/CONVENIENCE HOOKS
// ============================================

/**
 * Hook to get current user's full performance dashboard data
 */
export function useMyPerformanceDashboard(periodType: PeriodType = 'quarterly') {
  const params: PerformanceQueryParams = { periodType };

  const performanceQuery = useMyPerformance(params);
  const trendQuery = useMyTrend({ ...params, trendPeriods: 6 });

  return {
    performance: performanceQuery.data,
    trend: trendQuery.data,
    isLoading: performanceQuery.isLoading || trendQuery.isLoading,
    isError: performanceQuery.isError || trendQuery.isError,
    error: performanceQuery.error || trendQuery.error,
    refetch: () => {
      performanceQuery.refetch();
      trendQuery.refetch();
    },
  };
}

/**
 * Hook to get team performance dashboard data (for managers)
 */
export function useTeamPerformanceDashboard(periodType: PeriodType = 'quarterly') {
  const params: PerformanceQueryParams = { periodType };

  const teamQuery = useTeamPerformance(params);

  return {
    team: teamQuery.data,
    isLoading: teamQuery.isLoading,
    isError: teamQuery.isError,
    error: teamQuery.error,
    refetch: teamQuery.refetch,
  };
}

/**
 * Hook to get organization performance dashboard data (for admins)
 */
export function useOrgPerformanceDashboard(periodType: PeriodType = 'quarterly') {
  const params: PerformanceQueryParams = { periodType };

  const orgQuery = useOrganizationPerformance(params);

  return {
    organization: orgQuery.data,
    isLoading: orgQuery.isLoading,
    isError: orgQuery.isError,
    error: orgQuery.error,
    refetch: orgQuery.refetch,
  };
}
